import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SidebarLayout from '../layout/SidebarLayout';
import GymDashboard from './GymDashboard';
import AddMember from './AddMember';
import MembersManager from './MembersManager';
import MembersDraftRedirect from './MembersDraftRedirect';
import TrainerCalendarSimple from './TrainerCalendarSimple';
import GymStatistics from './GymStatistics';
import GymStatisticsIndividual from './GymStatisticsIndividual';
import GymSettings from './GymSettings';
import NotificationManager from '../notifications/NotificationManager';
import CoursesManager from './courses/CoursesManager';
import TemplatesManager from './templates/TemplatesManager';
import GroupsManager from './templates/GroupsManager';
import BulkEnrollmentPage from './bulk/BulkEnrollmentPage';
import FeedbackTab from './FeedbackTab';
import ImportCSV from './ImportCSV';
import LoadingSpinner from '../common/LoadingSpinner';
import GymExercisesManager from './exercises/GymExercisesManager';
import GymOpeningHours from './GymOpeningHours';
import GymFirstLoginSetupPage from './GymFirstLoginSetupPage';
import { GymBillingProvider, useGymBillingOptional } from '../../contexts/GymBillingContext';
import TrialFeatureBlur from './billing/TrialFeatureBlur';
import GymAccounting from './billing/GymAccounting';
import GymSocialPage from './social/GymSocialPage';
import GymEmailPage from './email/GymEmailPage';
import { isPathAllowedForGymPlan } from '../../utils/gymPlanAccess';
import { TRAINER_HOME_PATH } from '../../config/navigationConfig';

const isFirstLoginSetupPath = (pathname) =>
    pathname === '/gym/first-login-setup' || pathname.endsWith('/first-login-setup');

function GymPanelRoutes() {
    const { currentUser, isGym, isTrainer, getAuthenticatedApi } = useAuth();
    const billingCtx = useGymBillingOptional();
    const planAllowedPaths = billingCtx?.planAllowedPaths ?? billingCtx?.billing?.plan_allowed_paths ?? null;
    const location = useLocation();
    const [firstLoginChecked, setFirstLoginChecked] = useState(false);
    const [needsFirstLoginSetup, setNeedsFirstLoginSetup] = useState(
        () => Number(currentUser?.requires_first_login_setup) === 1,
    );

    useEffect(() => {
        if (!currentUser) {
            return;
        }
        setNeedsFirstLoginSetup(Number(currentUser.requires_first_login_setup) === 1);
    }, [currentUser?.requires_first_login_setup, currentUser?.id]);

    useEffect(() => {
        let cancelled = false;
        async function checkFirstLogin() {
            if (!currentUser || (!isGym() && !isTrainer())) {
                if (!cancelled) {
                    setFirstLoginChecked(true);
                    setNeedsFirstLoginSetup(false);
                }
                return;
            }
            try {
                const api = getAuthenticatedApi();
                const res = await api.get('/check_first_login.php');
                if (!cancelled) {
                    const needsSetup =
                        res.data?.first_login === true
                        || res.data?.requires_first_login_setup === true
                        || Number(currentUser.requires_first_login_setup) === 1;
                    setNeedsFirstLoginSetup(needsSetup);
                }
            } catch (err) {
                console.warn('check_first_login fallito:', err);
                if (!cancelled) {
                    setNeedsFirstLoginSetup(Number(currentUser.requires_first_login_setup) === 1);
                }
            } finally {
                if (!cancelled) {
                    setFirstLoginChecked(true);
                }
            }
        }
        checkFirstLogin();
        return () => {
            cancelled = true;
        };
    }, [currentUser, getAuthenticatedApi, isGym, isTrainer]);

    if (currentUser && (isGym() || isTrainer()) && !firstLoginChecked) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <LoadingSpinner />
            </div>
        );
    }

    const billingRenewalOnly = Boolean(currentUser?.gym_billing_renewal_required);
    const isContabilitaPath =
        location.pathname === '/gym/contabilita' || location.pathname.endsWith('/contabilita');

    if (billingRenewalOnly && !isContabilitaPath) {
        return <Navigate to="/gym/contabilita" replace />;
    }

    if (firstLoginChecked && needsFirstLoginSetup && !isFirstLoginSetupPath(location.pathname)) {
        return <Navigate to="/gym/first-login-setup" replace />;
    }

    if (firstLoginChecked && !needsFirstLoginSetup && isFirstLoginSetupPath(location.pathname)) {
        const homePath = isTrainer() ? TRAINER_HOME_PATH : '/gym';
        return <Navigate to={billingRenewalOnly ? '/gym/contabilita' : homePath} replace />;
    }

    if (
        planAllowedPaths
        && !billingCtx?.loading
        && !isFirstLoginSetupPath(location.pathname)
        && !isPathAllowedForGymPlan(location.pathname, planAllowedPaths)
    ) {
        return <Navigate to={isTrainer() ? TRAINER_HOME_PATH : '/gym'} replace />;
    }

    const gymHomeElement = isTrainer()
        ? <Navigate to={TRAINER_HOME_PATH} replace />
        : <GymDashboard />;

    return (
        <Routes>
            <Route path="first-login-setup" element={<GymFirstLoginSetupPage />} />
            <Route element={<SidebarLayout />}>
                <Route index element={gymHomeElement} />
                <Route path="members" element={<MembersManager />} />
                <Route path="members-draft" element={<MembersDraftRedirect />} />
                {isGym() && (
                    <Route
                        path="add-member"
                        element={
                            <TrialFeatureBlur routeKey="add-member">
                                <AddMember />
                            </TrialFeatureBlur>
                        }
                    />
                )}
                {isGym() && (
                    <Route
                        path="import-csv"
                        element={
                            <TrialFeatureBlur routeKey="import-csv">
                                <ImportCSV />
                            </TrialFeatureBlur>
                        }
                    />
                )}
                <Route
                    path="calendar"
                    element={
                        <TrialFeatureBlur routeKey="calendar">
                            <TrainerCalendarSimple />
                        </TrialFeatureBlur>
                    }
                />
                <Route
                    path="courses"
                    element={
                        <TrialFeatureBlur routeKey="courses">
                            <CoursesManager />
                        </TrialFeatureBlur>
                    }
                />
                {isGym() && (
                    <Route
                        path="bulk-enrollment"
                        element={
                            <TrialFeatureBlur routeKey="bulk-enrollment">
                                <BulkEnrollmentPage />
                            </TrialFeatureBlur>
                        }
                    />
                )}
                <Route
                    path="templates"
                    element={
                        <TrialFeatureBlur routeKey="templates">
                            <TemplatesManager />
                        </TrialFeatureBlur>
                    }
                />
                <Route
                    path="template-groups"
                    element={
                        <TrialFeatureBlur routeKey="template-groups">
                            <GroupsManager />
                        </TrialFeatureBlur>
                    }
                />
                {isGym() && (
                    <Route
                        path="exercises"
                        element={
                            <TrialFeatureBlur routeKey="exercises">
                                <GymExercisesManager />
                            </TrialFeatureBlur>
                        }
                    />
                )}
                {isGym() && <Route path="opening-hours" element={<GymOpeningHours />} />}
                <Route
                    path="statistics"
                    element={
                        <TrialFeatureBlur routeKey="statistics">
                            <GymStatistics />
                        </TrialFeatureBlur>
                    }
                />
                <Route
                    path="statistics-individual"
                    element={
                        <TrialFeatureBlur routeKey="statistics-individual">
                            <GymStatisticsIndividual />
                        </TrialFeatureBlur>
                    }
                />
                <Route path="notifications" element={<NotificationManager />} />
                {isGym() && <Route path="feedback" element={<FeedbackTab />} />}
                {isGym() && (
                    <Route
                        path="social"
                        element={
                            <TrialFeatureBlur routeKey="social">
                                <GymSocialPage />
                            </TrialFeatureBlur>
                        }
                    />
                )}
                {isGym() && (
                    <Route
                        path="email"
                        element={
                            <TrialFeatureBlur routeKey="email">
                                <GymEmailPage />
                            </TrialFeatureBlur>
                        }
                    />
                )}
                {isGym() && <Route path="contabilita" element={<GymAccounting />} />}
                {isGym() && <Route path="settings" element={<GymSettings />} />}
            </Route>
        </Routes>
    );
}

const ProtectedGymPanel = () => {
    const { currentUser, loading, isGym, isTrainer } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <LoadingSpinner />
            </div>
        );
    }

    if (!currentUser || (!isGym() && !isTrainer())) {
        return <Navigate to="/login" replace />;
    }

    return (
        <GymBillingProvider>
            <GymPanelRoutes />
        </GymBillingProvider>
    );
};

export default ProtectedGymPanel;
